import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { useCurrentIo } from '../auth/useImpersonation'
import { loadToyIndex } from '../upload/toyIndex'
import type { FeaturedToy } from '../types/toy'
import { entryToToy } from '../types/toyEntry'
import './FeaturedToys.css'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; toys: FeaturedToy[] }
  | { kind: 'error'; message: string }

export default function YourToys() {
  const { state: authState } = useAuth()
  const io = useCurrentIo()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  useEffect(() => {
    if (!io) return
    let cancelled = false

    loadToyIndex(io)
      .then((index) => {
        if (cancelled) return
        const toys = index
          .map(entryToToy)
          .filter((t): t is FeaturedToy => t !== null)
          .reverse()
        setState({ kind: 'ready', toys })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load'
        setState({ kind: 'error', message })
      })

    return () => {
      cancelled = true
    }
  }, [io])

  if (authState.status !== 'authenticated') return null
  if (state.kind === 'ready' && state.toys.length === 0) return null

  return (
    <section className="featured-toys">
      <h2>Your toys</h2>
      {state.kind === 'loading' && (
        <p className="featured-toys__status badge">
          <span className="bdot" />
          Loading your toys…
        </p>
      )}
      {state.kind === 'error' && (
        <p
          className="featured-toys__status featured-toys__status--error"
          role="alert"
        >
          Couldn't load your toys: {state.message}
        </p>
      )}
      {state.kind === 'ready' && state.toys.length > 0 && (
        <ul className="featured-toys__strip" aria-label="Your toys">
          {state.toys.map((toy) => (
            <li key={toy.id} className="featured-toys__card">
              <Link to={`/toys/my/${toy.id}`} className="featured-toys__link">
                {toy.thumbnailUrl ? (
                  <img
                    src={toy.thumbnailUrl}
                    alt={toy.title}
                    className="featured-toys__thumbnail"
                  />
                ) : (
                  <div className="featured-toys__art" aria-hidden="true">
                    {toy.title.charAt(0)}
                  </div>
                )}
                <h3 className="featured-toys__title">{toy.title}</h3>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
